import React from "react";

/**
 * Phase 12 polish — hide empty subtitle / specialization / bio so a
 * deliberately-slim advisor record (e.g. Dharam: title-only) renders as a
 * tight, premium card without placeholder gaps.
 */
export const AdvisorCard = ({ advisor }) => {
  // Payload rich-text bios can come back as "" after a save-to-empty; treat
  // anything that's falsy OR an empty/whitespace-only string as "not set".
  const has = (v) => typeof v === "string" ? v.trim().length > 0 : Boolean(v);

  // Strap shows only when the advisor record carries at least one detail
  // field (subtitle / specialization / bio). Title alone does NOT trigger
  // it — a title-only record (Dharam, Co-Founder) stays deliberately bare.
  const hasDetail =
    has(advisor.subtitle) || has(advisor.specialization) || has(advisor.bio);
  const showSubtitle = hasDetail
    ? has(advisor.subtitle)
      ? advisor.subtitle
      : "Licensed Insurance Professional"
    : null;

  const showSpecialization = has(advisor.specialization);
  const showBio = has(advisor.bio);
  const showDivider = showSpecialization || showBio;

  return (
    <article
      data-testid={`advisor-card-${advisor.id}`}
      className="lw-card group p-6 sm:p-7 flex flex-col items-center text-center h-full"
    >
      <div className="relative">
        <div
          aria-hidden="true"
          className="absolute -inset-1 rounded-full bg-gradient-to-br from-[#D4A24C]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        />
        <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-2 border-lw-gold/40 bg-lw-ivory-warm">
          <img
            src={advisor.headshot_url}
            alt={`Portrait of ${advisor.name}`}
            className="w-full h-full object-cover"
            loading="lazy"
            data-testid={`advisor-headshot-${advisor.id}`}
          />
        </div>
      </div>

      <h3 className="mt-5 font-serif-display text-xl text-lw-navy leading-tight">
        {advisor.name}
      </h3>

      {has(advisor.title) && (
        <p
          data-testid={`advisor-title-${advisor.id}`}
          className="mt-1 font-sans-ui text-[0.7rem] tracking-[0.18em] uppercase text-lw-gold"
        >
          {advisor.title}
        </p>
      )}

      {showSubtitle && (
        <p
          data-testid={`advisor-license-${advisor.id}`}
          className="mt-1 font-sans-ui text-[0.7rem] tracking-[0.14em] uppercase text-lw-muted"
        >
          {showSubtitle}
        </p>
      )}

      {showDivider && (
        <div className="gold-hairline w-12 my-4 opacity-70" />
      )}

      {showSpecialization && (
        <p className="text-sm text-lw-navy/80 font-medium leading-snug">
          {advisor.specialization}
        </p>
      )}
      {showBio && (
        <p className="mt-2 text-sm text-lw-muted leading-relaxed">
          {advisor.bio}
        </p>
      )}
    </article>
  );
};

export default AdvisorCard;
